import { useTranslation } from '../../../i18n/useTranslation';
import styles from './order-confirmation.module.scss';
import globalStyles from '../../../global-styles.module.scss';
import classNames from 'classnames';
import {
    extensionFiles,
    getPdfOrderSummary,
    htmlId,
    maxWidthDragAndDropFile,
    minHeightDragAndDropFile,
    pdfFileNameOrderSummary,
    submitOrders,
    uploadFileInfo,
} from './order-confirmation.service';
import { useNavigate } from 'react-router-dom';
import {
    ORDER_PATHS,
    PRODUCT_FIRST_LEVEL_PATHS,
    SHOPPING_CART_PATHS,
} from '../../../shared/constants/breadcrumb.constants';
import { useBetween } from 'use-between';
import { ShoppingCartUseBetween } from '../../../components/order/use-between/shopping-cart-use-between';
import {
    getTotalQuantityShoppingCart,
    mapAndGetInitialFormValues,
    uploadBondFile,
} from '../../../components/shopping-cart/services/shopping-cart.service';
import usePriceFormatter from '../../../hooks/usePriceFormatter';
import { IShopingCart } from '../../../components/order/interfaces/shopping-cart.interface';
import { toast } from 'react-toastify';
import { getCurrencySymbol } from '../../../components/order/services/currency.service';
import DragAndDropFile from '../../../shared/file-handler/drag-and-drop-file';
import { Utils } from '../../../shared/constants/utilities.constants';
import { CircularProgress, Tooltip } from '@mui/material';
import { HttpStatusCode } from 'axios';
import ShoppingCartInitialize from '../../../components/shopping-cart/shopping-cart-initialize';
import { getAndShowFileDocument } from '../../../shared/services/attachments.service';
import { DocumentFileContent } from '../../../shared/models/document-file-content.model';
import { PermissionsUseBetween } from '../../../shared/use-between/permissions-use-between';
import { ErpClaimCode } from '../../../shared/models/erp-claim-code.enum';
import ErrorAlert from '../../../shared/alerts/error-alert';
import { useState } from 'react';
import { getCommonErrorTranslate } from '../../../configuration/axios-configuration/axios.erros.interceptor.service';
import { FileDocumentTypeCode } from '../../../shared/models/file-document-type.enum';
import { claimIsActive } from '../../../shared/services/claims.service';

const OrderConfirmation: React.FC = () => {
    const { t } = useTranslation('translation');
    const { getNetPriceWithPrecision, getPriceWithPrecision } = usePriceFormatter();
    const navigate = useNavigate();
    const { buildingSite } = useBetween(ShoppingCartUseBetween);
    const { initialFormValues } = useBetween(ShoppingCartUseBetween);
    const { isSelfPickup } = useBetween(ShoppingCartUseBetween);
    const { shoppingCart } = useBetween(ShoppingCartUseBetween);
    const { setQuantityShoppingCart } = useBetween(ShoppingCartUseBetween);
    const { setShoppingCart } = useBetween(ShoppingCartUseBetween);
    const { isReadyToOrder, setIsReadyToOrder } = useBetween(ShoppingCartUseBetween);
    const { setInitialFormValues } = useBetween(ShoppingCartUseBetween);
    const { setIsSelfPickup } = useBetween(ShoppingCartUseBetween);
    const showError = (message: string) => toast.error(message);
    const { claims } = useBetween(PermissionsUseBetween);
    const [permissionAlertIsOpen, setOpenPermissionAlert] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPdfLoading, setIsPdfLoading] = useState(false);

    const getTranslate = (code: string) => {
        return t('FIXCENTER_ORDERING.' + code);
    };

    const handleSubmitOrders = () => {
        if (!claimIsActive(claims, ErpClaimCode.CUSTOMER_ECOM_ROLE)) {
            setOpenPermissionAlert(true);
        } else if (!isSubmitting) {
            setIsSubmitting(true);
            submitOrders()
                .then((response) => {
                    if (response) {
                        const data: IShopingCart = response.data;
                        setShoppingCart(data);
                        setQuantityShoppingCart(getTotalQuantityShoppingCart(data));
                        setIsReadyToOrder(data.isReadyToOrder);
                        setInitialFormValues(mapAndGetInitialFormValues(data.detail));
                        setIsSelfPickup(data.detail?.isSelfPickup);
                        setIsSubmitting(false);
                        navigate(ORDER_PATHS[1].link);
                    } else {
                        setIsSubmitting(false);
                        showError(getTranslate('MESSAGE_5'));
                    }
                })
                .catch(() => {
                    setIsSubmitting(false);
                    showError(getTranslate('MESSAGE_5'));
                });
        }
    };

    const handleGetPdfOrderSummary = () => {
        if (isPdfLoading) {
            return;
        }
        setIsPdfLoading(true);
        getPdfOrderSummary()
            .then((response) => {
                if (response) {
                    const model: DocumentFileContent = response.data;
                    if (!model.message) {
                        getAndShowFileDocument(model.file, pdfFileNameOrderSummary, FileDocumentTypeCode.PDF);
                    } else {
                        showError(getTranslate('MESSAGE_9'));
                    }
                } else {
                    showError(getTranslate('MESSAGE_9'));
                }
                setIsPdfLoading(false);
            })
            .catch(() => {
                setIsPdfLoading(false);
                showError(getTranslate('MESSAGE_9'));
            });
    };

    const getBondFileModelDto = (file: File): FormData => {
        const dto: FormData = new FormData();
        dto.append('BondFile', file, file.name);
        return dto;
    };

    const handleBondFileChange = (files: File[]): void | Promise<any> => {
        if (files && files.length > 0) {
            uploadBondFile(getBondFileModelDto(files[0]))
                .then((response) => {
                    if (response && response.status === HttpStatusCode.Ok) {
                        const data: IShopingCart = response.data;
                        setShoppingCart(data);
                    } else {
                        showError(t('commonErrors.UPLOAD_FILE_MESSAGE'));
                    }
                })
                .catch(() => showError(t('commonErrors.UPLOAD_FILE_MESSAGE')));
        }
    };

    const bondFileFileExist = (): boolean => {
        return !Utils.isNullOrUndefined(shoppingCart.bondFile);
    };

    return (
        <>
            <div className={styles.container}>
                <ErrorAlert
                    isOpen={permissionAlertIsOpen}
                    handleCloseDialog={(val: boolean) => setOpenPermissionAlert(val)}
                    translateCode={getCommonErrorTranslate(ErpClaimCode[ErpClaimCode.CUSTOMER_ECOM_ROLE])}
                ></ErrorAlert>
                <ShoppingCartInitialize></ShoppingCartInitialize>
                <div className={globalStyles.space_between_item}>
                    <label className={styles.h1}>{t('ORDER_CONFIRMATION')}</label>
                    <section className={globalStyles.float_left_item}>
                        <button
                            id={`${htmlId}-go-back-to-cart-btn`}
                            className={classNames(globalStyles.defult_btn, globalStyles.mr_2)}
                            onClick={() => {
                                navigate(SHOPPING_CART_PATHS[1].link);
                            }}
                        >
                            {t('GO_BACK_TO_CART')}
                        </button>
                        <button
                            id={`${htmlId}-continue-shopping-btn`}
                            className={classNames(globalStyles.defult_btn, globalStyles.mr_2)}
                            onClick={() => {
                                navigate(PRODUCT_FIRST_LEVEL_PATHS[1].link);
                            }}
                        >
                            {t('CONTINUE_SHOPPING')}
                        </button>
                        <Tooltip
                            disableHoverListener={
                                !claimIsActive(claims, ErpClaimCode.CUSTOMER_BDC_ROLE) ? true : bondFileFileExist()
                            }
                            placement={'bottom'}
                            title={t(getTranslate('MESSAGE_7'))}
                        >
                            <button
                                id={`${htmlId}-finish-order-btn`}
                                disabled={
                                    isSubmitting ||
                                    (claimIsActive(claims, ErpClaimCode.CUSTOMER_BDC_ROLE)
                                        ? !isReadyToOrder || !bondFileFileExist()
                                        : !isReadyToOrder)
                                }
                                className={classNames(globalStyles.defult_btn)}
                                onClick={handleSubmitOrders}
                            >
                                {isSubmitting && (
                                    <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                                )}
                                {t('FINISH_ORDER')}
                            </button>
                        </Tooltip>
                    </section>
                </div>

                <div className={classNames(globalStyles.float_left_item, globalStyles.mt_4, globalStyles.mb_3)}>
                    <label className={styles.h2}>{t(getTranslate('MESSAGE_2'))}:</label>
                </div>

                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('BUILDING_SITE')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>
                            {buildingSite && buildingSite.code + ' | ' + buildingSite.name}
                        </label>
                    </div>
                </div>

                {/* <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('CONTACT')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{initialFormValues.contact}</label>
                    </div>
                </div>

                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('PHONE')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{initialFormValues.phone}</label>
                    </div>
                </div> */}

                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('REFERENCE')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{initialFormValues.reference}</label>
                    </div>
                </div>

                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('ISSELFPICKUP')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{isSelfPickup ? t('Yes') : t('No')}</label>
                    </div>
                </div>

                <div className={classNames(styles.columns, globalStyles.mb_3)}>
                    <div className={styles.column1}>
                        <label className={classNames(styles.h2)}>{t('COMMENTS')}:</label>
                    </div>
                    <div className={classNames(styles.column2, globalStyles.float_left_item)}>
                        <label className={classNames(styles.h3)}>{initialFormValues.comments}</label>
                    </div>
                </div>

                <button
                    id={`${htmlId}-download-pdf-with-summary-btn`}
                    disabled={isPdfLoading || !isReadyToOrder}
                    className={classNames(globalStyles.defult_btn, globalStyles.mr_2)}
                    onClick={() => {
                        handleGetPdfOrderSummary();
                    }}
                >
                    {isPdfLoading && <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />}
                    {t(getTranslate('MESSAGE_8'))}
                </button>

                <label
                    className={classNames(
                        styles.h1,
                        globalStyles.float_left_item,
                        globalStyles.mt_4,
                        globalStyles.mb_4
                    )}
                >
                    {t('SUMMARY')}
                </label>
                <div className={styles.order_header}>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('VARIANT_NAME')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('PACKAGING')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('UNIT_PRICE')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('QUANTITY')}</label>
                    </div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h2)}>{t('NET_PRICE')}</label>
                    </div>
                </div>
                <div className={styles.order_content}>
                    {shoppingCart &&
                        shoppingCart.products &&
                        shoppingCart.products.map((item, index) => (
                            <div
                                className={`${classNames(styles.row_content, globalStyles.w_100)} ${index % 2 !== 0 ? styles.shading_row : ''}`}
                                key={index}
                            >
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3, globalStyles.mr_3)}>
                                        {item.productName}
                                    </label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3)}>{item.packaging}</label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3, globalStyles.mr_1)}>
                                        {getNetPriceWithPrecision(item.netto)}
                                    </label>
                                    <label className={classNames(styles.h3)}>
                                        {getCurrencySymbol(item.saleCurrencyName)}
                                    </label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3)}>{item.quantity}</label>
                                </div>
                                <div className={styles.column}>
                                    <label className={classNames(styles.h3, globalStyles.mr_1)}>
                                        {getNetPriceWithPrecision(item.nettoTotalPrice)}
                                    </label>
                                    <label className={classNames(styles.h3)}>
                                        {getCurrencySymbol(item.saleCurrencyName)}
                                    </label>
                                </div>
                            </div>
                        ))}
                </div>
                <div
                    className={`${classNames(styles.row_content, globalStyles.w_100)} ${shoppingCart.products.length % 2 !== 0 ? styles.shading_row : ''}`}
                >
                    <div className={styles.column}>
                        <label className={classNames(styles.h3)}>{t('SHIPPING_COST')}</label>
                    </div>
                    <div className={styles.column}></div>
                    <div className={styles.column}></div>
                    <div className={styles.column}></div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h3, globalStyles.mr_1)}>
                            {getPriceWithPrecision(shoppingCart.shippingCost)}
                        </label>
                        <label className={classNames(styles.h3)}>
                            {Array.isArray(shoppingCart.products) && shoppingCart.products.length > 0
                                ? getCurrencySymbol(shoppingCart.products[0].saleCurrencyName)
                                : '_'}
                        </label>
                    </div>
                </div>
                <div className={classNames(styles.row_content, globalStyles.w_100)}>
                    <div className={styles.column}>
                        <label className={classNames(styles.h4)}>{t('TOTAL')}</label>
                    </div>
                    <div className={styles.column}></div>
                    <div className={styles.column}></div>
                    <div className={styles.column}></div>
                    <div className={styles.column}>
                        <label className={classNames(styles.h3, globalStyles.mr_1)}>
                            {getPriceWithPrecision(shoppingCart.nettoTotalPrice)}
                        </label>
                        <label className={classNames(styles.h3)}>
                            {Array.isArray(shoppingCart.products) && shoppingCart.products.length > 0
                                ? getCurrencySymbol(shoppingCart.products[0].saleCurrencyName)
                                : '_'}
                        </label>
                    </div>
                </div>
                <label
                    className={classNames(
                        styles.h1,
                        globalStyles.float_left_item,
                        globalStyles.mt_4,
                        globalStyles.mb_3
                    )}
                >
                    {t(getTranslate('HEADER_2'))}
                </label>
                <div className={classNames(globalStyles.mb_3)}>
                    <label className={classNames(globalStyles.float_left_item, styles.h3, globalStyles.mb_1)}>
                        {t(getTranslate('MESSAGE_6_1'))}
                    </label>
                    <label className={classNames(globalStyles.float_left_item, styles.h3, globalStyles.mb_1)}>
                        {t(getTranslate('MESSAGE_6_2'))}
                    </label>
                    <label className={classNames(globalStyles.float_left_item, styles.h3, globalStyles.mb_1)}>
                        {t(getTranslate('MESSAGE_6_3'))}
                    </label>
                </div>
                <div className={classNames(globalStyles.float_left_item)}>
                    <DragAndDropFile
                        handleFileChange={handleBondFileChange}
                        maxWidth={maxWidthDragAndDropFile}
                        minHeight={minHeightDragAndDropFile}
                        fileNameInput={shoppingCart.bondFile?.fileName}
                        htmlId={htmlId + '-upload-bond-file'}
                        isValid={bondFileFileExist()}
                        uploadFileInfo={uploadFileInfo}
                        extensionFiles={extensionFiles}
                        disabled={isSubmitting}
                    />
                </div>
                <section className={classNames(globalStyles.float_right_item, globalStyles.mt_3)}>
                    <button
                        id={`${htmlId}-go-back-to-cart-btn`}
                        className={classNames(globalStyles.defult_btn, globalStyles.mr_2)}
                        onClick={() => {
                            navigate(SHOPPING_CART_PATHS[1].link);
                        }}
                    >
                        {t('GO_BACK_TO_CART')}
                    </button>
                    <button
                        id={`${htmlId}-continue-shopping-btn`}
                        className={classNames(globalStyles.defult_btn, globalStyles.mr_2)}
                        onClick={() => {
                            navigate(PRODUCT_FIRST_LEVEL_PATHS[1].link);
                        }}
                    >
                        {t('CONTINUE_SHOPPING')}
                    </button>

                    <Tooltip
                        disableHoverListener={
                            !claimIsActive(claims, ErpClaimCode.CUSTOMER_BDC_ROLE) ? true : bondFileFileExist()
                        }
                        placement={'bottom'}
                        title={t(getTranslate('MESSAGE_7'))}
                    >
                        <button
                            id={`${htmlId}-finish-order-btn`}
                            disabled={
                                isSubmitting ||
                                (claimIsActive(claims, ErpClaimCode.CUSTOMER_BDC_ROLE)
                                    ? !isReadyToOrder || !bondFileFileExist()
                                    : !isReadyToOrder)
                            }
                            className={classNames(globalStyles.defult_btn)}
                            onClick={handleSubmitOrders}
                        >
                            {isSubmitting && <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />}
                            {t('FINISH_ORDER')}
                        </button>
                    </Tooltip>
                </section>
            </div>
        </>
    );
};

export default OrderConfirmation;
